import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createApiClient } from '../lib/apiClient';
import { verifyEmailOtp, resendEmailOtp, verifySmsOtp } from '../lib/auth/authApi';
import './auth.css';

const bare = createApiClient({ tokens: { get: () => null, set: () => {} } });

export default function VerifyOtpScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = params.get('email') ?? '';
  const phone = params.get('phone') ?? '';

  const [code, setCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsDone, setSmsDone] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  async function onVerifyEmail(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { verified, enrollmentTicket } = await verifyEmailOtp(bare, email, code.trim());
      if (!verified) { setError('That code did not match. Try again.'); return; }
      // Carry the enrollment ticket via router state (never the URL) — the
      // next screen needs it to prove, at passkey enroll time, that THIS
      // browser is the one that just verified the email (S6c 1b CRITICAL
      // fix; see src/api/auth/enrollment-ticket.ts).
      navigate(`/enroll?email=${encodeURIComponent(email)}`, { state: { enrollmentTicket } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    } finally { setBusy(false); }
  }

  async function onResendEmail() {
    setResending(true); setError(''); setNotice('');
    try {
      await resendEmailOtp(bare, email);
      setNotice('A new code is on its way.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the code.');
    } finally { setResending(false); }
  }

  async function onVerifySms() {
    try {
      const { verified } = await verifySmsOtp(bare, phone, smsCode.trim());
      setSmsDone(verified);
    } catch { /* SMS is optional in M1; ignore failures */ }
  }

  return (
    <section className="auth">
      <div className="auth__card">
        <p className="auth__kicker">Verify it's you</p>
        <h1 className="auth__title">Check your email.</h1>
        <p className="auth__sub">We sent a six-digit code to {email}.</p>
        <p className="auth__alert" role="alert">{error}</p>
        <p className="auth__notice" role="status">{notice}</p>
        <form onSubmit={onVerifyEmail}>
          <label className="auth__field">
            <span className="auth__label">Email code</span>
            <input className="auth__input" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                   value={code} onChange={(e) => setCode(e.target.value)} required />
          </label>
          <div className="la-cta-wrap">
            <button className="la-btn-primary" type="submit" disabled={busy}>{busy ? 'Verifying…' : 'Verify'}</button>
          </div>
        </form>
        <button className="la-btn-secondary" type="button" style={{ marginTop: 12 }}
                disabled={resending} onClick={() => { void onResendEmail(); }}>
          {resending ? 'Resending…' : 'Resend code'}
        </button>

        {phone !== '' && (
          <div className="auth__optional">
            <span className="auth__label">Optional — code from your phone</span>
            <input className="auth__input" inputMode="numeric" maxLength={6}
                   value={smsCode} onChange={(e) => setSmsCode(e.target.value)} />
            <button className="la-btn-secondary" type="button" style={{ marginTop: 12 }} onClick={onVerifySms}>
              {smsDone ? 'Phone verified ✓' : 'Verify phone'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
